import Keycloak from 'keycloak-js'

type KeycloakReadyResolve = (authenticated: boolean) => void

export default defineNuxtPlugin({
  name: 'keycloak',

  parallel: true,

  dependsOn: ['toast', 'store'],

  hooks: {
    /**
     * Fix router issue where there are a hanging ?state url params, see : https://github.com/keycloak/keycloak/issues/14742
     */
    'app:created'() {
      const router = useRouter()
      const currentQuery = { ...router.currentRoute.value.query }

      // Delete keycloak authentication parameters from url
      delete currentQuery.session_state
      delete currentQuery.iss
      delete currentQuery.state
      delete currentQuery.code
      router.currentRoute.value.query = currentQuery
    },
  },

  setup({ $config, $toast, $store }) {
    const route = useRoute()

    const keycloak = new Keycloak({
      url: $config.public.keycloakUrl,
      realm: $config.public.keycloakRealm,
      clientId: $config.public.keycloakClientId,
    })

    // Create a promise that resolves when Keycloak is ready
    let keycloakReadyResolve!: KeycloakReadyResolve
    const keycloakReadyPromise: Promise<boolean> = new Promise((resolve) => {
      keycloakReadyResolve = resolve
    })

    // Resolve the promise when ready
    keycloak.onReady = authenticated => keycloakReadyResolve(authenticated ?? false)

    keycloak.logoutUser = () => {
      const redirectUri = $config.public.logoutRedirectUrl

      $store.auth.logout()
      $store.companies.reset()
      return keycloak.logout({ redirectUri })
    }

    keycloak.onAuthSuccess = async () => {
      console.log('Authentication success')
    }

    keycloak.onAuthRefreshSuccess = async () => {
      console.log('Refresh success')
    }

    keycloak.refresh = async () => {
      await keycloak.updateToken(30)
        .then(() => {
          $store.auth.setTokens({
            refresh_token: keycloak.refreshToken,
            refresh_exp: keycloak.refreshTokenParsed?.exp || 0,
          })
        })
        .catch(() => {
          console.error('Failed to refresh token')
        })
    }
    keycloak.onTokenExpired = () => {
      try {
        keycloak.refresh()
      }
      catch (errors) {
        console.log('Failed to refresh token', errors)
        keycloak.logoutUser()
      }
    }

    const skipSSOCheck = route.path === '/accept-invite'

    keycloak
      .init({
        onLoad: skipSSOCheck ? undefined : 'check-sso',
        responseMode: 'query',
        scope: 'openid profile offline_access',
        // Check if stored refreshToken has expired, if not include in options
        ...((($store.auth.tokens.refresh_exp === 0 && $store.auth.tokens.refresh_token) || ($store.auth.tokens.refresh_exp > Math.floor(Date.now() / 1000))) && { refreshToken: $store.auth.tokens.refresh_token }),
      })
      .then(async (authenticated) => {
        if (authenticated) {
          if (!keycloak.token) {
            throw Error('Missing keycloak token')
          }
        }
      })
      .catch((error) => {
        console.error(error)
        $toast.error(`Error in our authentication services: ${error.message}`, {
          timeout: 5000,
          position: 'top-right',
        })
      })

    return {
      provide: { keycloak, keycloakReadyPromise },
    }
  },
})
